// The in-memory API's deploys and template overrides (DC-04; services/api/src/routes/
// operator-deploys.ts and company-deploys.ts), with the real routes' rules:
//
// - Agency console: an agency user with an operator grant on the target company; anyone else
//   404. Staging needs an `Idempotency-Key` (400 `idempotency_key_required`) and replays its first
//   answer; the version must be published and visible to the caller's agency (404 otherwise; a
//   draft is 422 `version_not_published`); the DE may have no open deploy (409
//   `deploy_already_open`). The bundle is refused (422 `bundle_refused`, every reason) when the
//   effective standing orders are empty, the map is empty, a role uses the `subscription` path or
//   a model the gateway does not serve (`FAKE_GATEWAY_MODELS`). `kind` is `first` unless the
//   version is already confirmed in the company; a first deploy waits in `staged` for a person's
//   approval, a redeploy or rollback goes straight to hand-off.
// - Hand-off: the real worker posts to AiGentPanel and the panel's callback confirms. Here time
//   does it: `HANDOFF_AFTER_MS` after approval (or staging, for a redeploy) the deploy is
//   `handed_off`, `CONFIRM_AFTER_MS` later `confirmed` with a panel_ref, and the DE's deploy
//   mirror (ADI-02) moves to the new version. The seed holds a failed deploy for its reasons.
// - Company side: managers read; the owner or the company's agency operator approves or rejects
//   (an admin is 403); the override is read by a manager or the seat's supervisor and set by an
//   owner, admin or the supervisor (an agency operator reads only, 403).
// - DC-05 (domain/deploys/override.ts): an managed draft with no verification skill stages with no
//   bundle and waits; the operator holding `verification_override` gives a reason and a future
//   `pilot_until` (403 without the relation; 422 `verification_override_refused` for a version
//   with a verification skill; 409 when overridden already or not staged), which builds the
//   unverified bundle. Approval and hand-off wait for it; at `pilot_until` an open deploy fails
//   `override_expired`. Company-side rows carry the template name, version and the override.
import type { RouteId } from "@companyos/sdk";
import { type Ctx, type Handler, json, notFound, type Principal, page, problem } from "./fake";
import { COMPANY_IDS, type FakeDe, uid } from "./fake-data";
import { FAKE_AGENCY_ID, type FakeCatalog, TEMPLATE_IDS, VERSION_IDS } from "./fake-templates";
import type {
  CompanyDeploy,
  ConfigurationBundle,
  Deploy,
  DeTemplateOverride,
  MeResponse,
  VerificationOverride,
} from "./types";

/** The gateway's model allow-list in the fake (LITELLM_MODELS). */
export const FAKE_GATEWAY_MODELS = ["gpt-4o", "claude-sonnet", "claude-haiku"] as const;
/** Simulated hand-off: approval → handed_off → confirmed. */
export const HANDOFF_AFTER_MS = 1500;
export const CONFIRM_AFTER_MS = 2500;

/** The seeded deploys' ids (tests and e2e reach the rows through them). */
export const DEPLOY_IDS = {
  rileyConfirmed: uid(0x7e00, 1),
  beaStaged: uid(0x7e00, 2),
  gusFailed: uid(0x7e00, 3),
  /** DC-05: Sam's pilot of the interview scheduler, overridden, waiting for the owner. */
  samPilot: uid(0x7e00, 4),
} as const;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const OPEN = new Set<Deploy["state"]>(["staged", "approved", "handed_off"]);
const SEEDED = "2026-09-10T09:00:00.000Z";

type Problemish = { path: string; code: string; message: string };

function problemWith(
  status: number,
  code: string,
  title: string,
  detail: string,
  errors?: Problemish[],
): Response {
  return new Response(
    JSON.stringify({
      type: `https://companyos.app/errors/${code}`,
      title,
      status,
      code,
      request_id: `fake-${Math.random().toString(36).slice(2, 10)}`,
      detail,
      ...(errors ? { errors } : {}),
    }),
    { status, headers: { "content-type": "application/problem+json" } },
  );
}
const invalid = (path: string, code: string, message: string) =>
  problemWith(422, "validation_failed", "Validation failed", message, [{ path, code, message }]);
const forbidden = (detail: string) => problemWith(403, "forbidden", "Forbidden", detail);
const conflict = (detail: string) =>
  problemWith(409, "deploy_state_conflict", "The deploy is not in a state for that", detail);

/** A stable 64-hex digest (the fake's stand-in for sha256; never compared with a real one). */
function digest(text: string): string {
  let out = "";
  for (let round = 0; out.length < 64; round++) {
    let h = 0x811c9dc5 ^ round;
    for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193);
    out += (h >>> 0).toString(16).padStart(8, "0");
  }
  return out.slice(0, 64);
}

type Gateway = (typeof FAKE_GATEWAY_MODELS)[number];
type RoleModelMap = DeTemplateOverride["role_model_map"] & {};

/** The map's problems through the gateway (domain/deploys/gateway.ts). */
export function modelMapProblems(map: RoleModelMap, prefix = "role_model_map"): Problemish[] {
  const out: Problemish[] = [];
  for (const [role, entry] of Object.entries(map).sort(([a], [b]) => (a < b ? -1 : 1))) {
    if (entry.paths.includes("subscription"))
      out.push({
        path: `${prefix}.${role}.paths`,
        code: "model_path_subscription_refused",
        message: "a client company's DE reaches its models through the gateway only",
      });
    if (!FAKE_GATEWAY_MODELS.includes(entry.model as Gateway))
      out.push({
        path: `${prefix}.${role}.model`,
        code: "model_not_on_gateway",
        message: `the gateway does not serve ${entry.model}`,
      });
  }
  return out;
}

/** DC-11: the self-serve routes (the `agents` add-on; fake.ts gates them like the API). */
export const FAKE_SELF_SERVE_ROUTES: ReadonlySet<RouteId> = new Set<RouteId>([
  "agents.selfServeTemplates",
  "agents.deploySelfServe",
]);

export function createDeployHandlers(deps: {
  now: () => Date;
  catalog: () => FakeCatalog | null;
  principalFor: (me: MeResponse, companyId: string | undefined) => Principal | null;
  desOf: (companyId: string) => FakeDe[];
  /** DC-11: whether the seat has an active day, week or month budget (the agents twin's). */
  seatHasBudget?: (companyId: string, seatId: string) => boolean;
  /** MODEL-POLICY: the template overrides, read by Setup › Model policy (fake-model-policy.ts). */
  shareOverrides?: (overrides: readonly DeTemplateOverride[]) => void;
  /**
   * DE-WIZARD: the deploys, read by the DEs' readiness (fake-data.ts `deReadiness`), and the
   * clock's tick (hand-off and confirm happen as time passes, whoever reads next).
   */
  shareDeploys?: (deploys: readonly Deploy[], tick: () => void) => void;
}): Partial<Record<RouteId, Handler>> {
  const at = () => deps.now().toISOString();
  let seq = 0x100;
  const deploys: Deploy[] = [];
  const bundles: ConfigurationBundle[] = [];
  /** DE-WIZARD: each bundle's standing orders as built (the override is edited afterwards). */
  const bundleOrders = new Map<string, string | null>();
  const overrides: DeTemplateOverride[] = [];
  deps.shareOverrides?.(overrides);
  deps.shareDeploys?.(deploys, () => tick());
  /**
   * DE-WIZARD (domain/deploys/stage.ts `overrideOf`): the DE's override of this template, else
   * its own from before any template (`template_id` null).
   */
  const overrideFor = (companyId: string, deId: string, templateId: string | null) =>
    overrides.find(
      (o) =>
        o.company_id === companyId && o.de_membership_id === deId && o.template_id === templateId,
    ) ??
    overrides.find(
      (o) => o.company_id === companyId && o.de_membership_id === deId && o.template_id === null,
    ) ??
    null;
  const pilots: VerificationOverride[] = [];
  const replays = new Map<string, Response>();

  const deOf = (companyId: string, id: string | undefined) =>
    deps.desOf(companyId).find((d) => d.membership_id === id?.toLowerCase()) ?? null;

  // Seed ----------------------------------------------------------------------------------------

  const GATEWAY_MAP: RoleModelMap = {
    lead: { model: "gpt-4o", paths: ["gateway"] },
    review: { model: "claude-sonnet", paths: ["gateway"] },
  };
  const coordinator = uid(0x7d00, 1);
  const addOverride = (company: string, de: string, n: number) =>
    overrides.push({
      company_id: company,
      id: uid(0x7e30, n),
      de_membership_id: de,
      template_id: coordinator,
      standing_orders_text: null,
      role_model_map: GATEWAY_MAP,
      version: 1,
      actor_kind: "person",
      actor_membership_id: null,
      created_at: SEEDED,
      updated_at: SEEDED,
    });
  const riley = uid(0x1000, 0xa01);
  const bea = uid(0x1000, 0xb01);
  const gus = uid(0x1000, 0xc01);
  addOverride(COMPANY_IDS.acme, riley, 1);
  addOverride(COMPANY_IDS.beta, bea, 2);
  addOverride(COMPANY_IDS.gamma, gus, 3);

  const operatorUser = (me: MeResponse) => me.user.id;
  const bundleFor = (
    companyId: string,
    versionId: string,
    override: DeTemplateOverride | null,
    files: string[],
    by: string,
    createdAt: string,
    /** DC-05: the pilot's override id; its facts are in the manifest, so in the sha. */
    pilotId?: string,
    /** DC-11: the empty self-serve agent's bundle is unverified (no verification skill). */
    empty = false,
  ): ConfigurationBundle => {
    const sha = digest(
      `${companyId}:${versionId}:${override?.id ?? "-"}:${override?.version ?? 0}:${pilotId ?? "-"}`,
    );
    const existing = bundles.find((b) => b.company_id === companyId && b.bundle_sha === sha);
    if (existing) return existing;
    const verification = pilotId || empty ? "unverified" : "verified";
    // DE-WIZARD: the standing orders the bundle carries (its `standing-orders.md`).
    const ordersText =
      override?.standing_orders_text ??
      deps.catalog()?.versionText(versionId)?.standing_orders_text ??
      null;
    const b: ConfigurationBundle = {
      id: uid(0x7e10, ++seq),
      template_version_id: versionId,
      company_id: companyId,
      de_template_override_id: override?.id ?? null,
      override_hash: override ? digest(`override:${override.id}:${override.version}`) : null,
      bundle_sha: sha,
      object_key: null,
      skill_index_tokens: 412,
      verification_state: verification,
      validation_report: {
        content_hash: digest(`content:${versionId}`),
        skill_index_tokens: 412,
        skill_index_token_cap: 2000,
        token_counter: "o200k_base",
        verification_state: verification,
        overridden_fields: override
          ? [
              ...(override.standing_orders_text != null ? ["standingOrders"] : []),
              ...(override.role_model_map != null ? ["roleModelMap"] : []),
            ]
          : [],
        model_map_checked_on_gateway: true,
        lead_model: "gpt-4o",
        files: files.map((path) => ({ path, sha256: digest(`${sha}:${path}`) })),
      },
      validated_at: createdAt,
      built_by: { kind: "user", user_id: by },
      created_at: createdAt,
    };
    bundleOrders.set(b.id, ordersText);
    bundles.push(b);
    return b;
  };
  const FILES = [
    "evals.json",
    "manifest.json",
    "role-model-map.json",
    "rules.json",
    "runtime-config.json",
    "skill-index.md",
    "standing-orders.md",
    "tool-grants.json",
  ];
  /** The demo operator (fake.ts USER_IDS.operator) staged and approved the seeded deploys. */
  const OPERATOR = "0193a0a0-0000-7000-8000-0000000000a1";
  const base = (
    d: Pick<Deploy, "id" | "target_company_id" | "de_membership_id" | "state" | "kind"> &
      Partial<Deploy>,
  ): Deploy => {
    const created = d.created_at ?? SEEDED;
    const override =
      overrides.find(
        (o) => o.company_id === d.target_company_id && o.de_membership_id === d.de_membership_id,
      ) ?? null;
    const bundle =
      d.bundle_id !== undefined
        ? null
        : bundleFor(
            d.target_company_id,
            VERSION_IDS.coordinatorV1,
            override,
            FILES,
            OPERATOR,
            created,
          );
    return {
      path: "done_for_you",
      agency_id: FAKE_AGENCY_ID,
      template_id: coordinator,
      template_version_id: VERSION_IDS.coordinatorV1,
      bundle_id: bundle?.id ?? null,
      staged_by: { kind: "user", user_id: OPERATOR },
      approved_by_user_id: null,
      approved_by_membership_id: null,
      approved_at: null,
      rejected_reason: null,
      handed_off_at: null,
      handoff_attempts: 0,
      panel_ref: null,
      confirmed_at: null,
      failed_at: null,
      error: null,
      previous_deploy_id: null,
      reconcile_state: null,
      reconcile_until: null,
      created_at: created,
      updated_at: created,
      ...d,
    };
  };
  const panelRef = (deId: string) => ({
    instance_id: `inst-${deId.slice(-6)}`,
    profile_name: `cos-${deId.replace(/-/g, "")}`,
    node_id: "node-fake-1",
    api_base_url: "https://panel.fake.test/api",
  });
  deploys.push(
    base({
      id: DEPLOY_IDS.rileyConfirmed,
      target_company_id: COMPANY_IDS.acme,
      de_membership_id: riley,
      state: "confirmed",
      kind: "first",
      created_at: "2026-09-08T09:00:00.000Z",
      approved_by_user_id: OPERATOR,
      approved_at: "2026-09-08T09:05:00.000Z",
      handed_off_at: "2026-09-08T09:05:10.000Z",
      handoff_attempts: 1,
      panel_ref: panelRef(riley),
      confirmed_at: "2026-09-08T09:07:30.000Z",
      updated_at: "2026-09-08T09:07:30.000Z",
    }),
    base({
      id: DEPLOY_IDS.gusFailed,
      target_company_id: COMPANY_IDS.gamma,
      de_membership_id: gus,
      state: "failed",
      kind: "first",
      created_at: "2026-09-09T09:00:00.000Z",
      approved_by_user_id: OPERATOR,
      approved_at: "2026-09-09T09:02:00.000Z",
      handed_off_at: "2026-09-09T09:02:12.000Z",
      handoff_attempts: 1,
      failed_at: "2026-09-09T09:06:40.000Z",
      updated_at: "2026-09-09T09:06:40.000Z",
      error: {
        code: "health_check_failed",
        message: "the DE's container did not answer its health check within 120 s",
      },
    }),
    base({
      id: DEPLOY_IDS.beaStaged,
      target_company_id: COMPANY_IDS.beta,
      de_membership_id: bea,
      state: "staged",
      kind: "first",
      created_at: SEEDED,
    }),
  );
  // DC-05: Sam's pilot of the interview scheduler (no verification skill) in Acme: the operator
  // overrode verification, so the unverified bundle exists; the owner has not approved it yet.
  const samPilot = uid(0x7e40, 1);
  const samBundle = bundleFor(
    COMPANY_IDS.acme,
    VERSION_IDS.pilotV1,
    null,
    FILES,
    OPERATOR,
    SEEDED,
    samPilot,
  );
  pilots.push({
    id: samPilot,
    deploy_id: DEPLOY_IDS.samPilot,
    bundle_id: samBundle.id,
    target_company_id: COMPANY_IDS.acme,
    operator_user_id: OPERATOR,
    reason: "Six-week pilot with the Acme recruiting team while the verification skill is built.",
    pilot_until: "2026-12-15T00:00:00.000Z",
    created_at: SEEDED,
  });
  deploys.push(
    base({
      id: DEPLOY_IDS.samPilot,
      target_company_id: COMPANY_IDS.acme,
      de_membership_id: uid(0x1000, 0xa02),
      state: "staged",
      kind: "first",
      template_id: TEMPLATE_IDS.pilot,
      template_version_id: VERSION_IDS.pilotV1,
      bundle_id: samBundle.id,
      created_at: SEEDED,
    }),
  );

  const pilotOf = (d: Pick<Deploy, "id">) => pilots.find((p) => p.deploy_id === d.id) ?? null;
  const pilotOver = (d: Pick<Deploy, "id">) => {
    const p = pilotOf(d);
    return p !== null && Date.parse(p.pilot_until) <= deps.now().getTime();
  };
  /** The catalog version deploys only as a pilot (an managed draft with no verification skill). */
  const needsPilot = (
    v: { state: string; verification_skill_pack_id: string | null },
    cls: string,
  ) => cls === "managed" && v.state === "draft" && v.verification_skill_pack_id === null;

  // Time: the simulated worker and the panel's callback ----------------------------------------

  function tick() {
    const now = deps.now().getTime();
    for (const d of deploys) {
      // DC-05: the expiry job fails a deploy still open under an expired pilot override.
      if ((d.state === "staged" || d.state === "approved") && pilotOver(d)) {
        const p = pilotOf(d);
        d.state = "failed";
        d.failed_at = p?.pilot_until ?? at();
        d.updated_at = d.failed_at;
        d.error = {
          code: "override_expired",
          message: `the pilot's verification override ended at ${p?.pilot_until} before the hand-off`,
        };
        continue;
      }
      const due =
        d.bundle_id !== null &&
        (d.state === "approved" ||
          (d.state === "staged" && !(d.path === "done_for_you" && d.kind === "first")));
      const since = Date.parse(d.approved_at ?? d.created_at);
      if (due && now - since >= HANDOFF_AFTER_MS) {
        const t = new Date(since + HANDOFF_AFTER_MS).toISOString();
        d.state = "handed_off";
        d.handed_off_at = t;
        d.handoff_attempts += 1;
        d.updated_at = t;
      }
      if (d.state === "handed_off" && d.handed_off_at) {
        const off = Date.parse(d.handed_off_at);
        if (now - off >= CONFIRM_AFTER_MS) {
          const t = new Date(off + CONFIRM_AFTER_MS).toISOString();
          d.state = "confirmed";
          d.panel_ref = panelRef(d.de_membership_id);
          d.confirmed_at = t;
          d.updated_at = t;
          const de = deOf(d.target_company_id, d.de_membership_id);
          const b = bundles.find((x) => x.id === d.bundle_id);
          const p = pilotOf(d);
          if (de && b)
            de.deploy = {
              template_version_id: d.template_version_id,
              bundle_sha: b.bundle_sha,
              deploy_ref: d.id,
              verification_state: b.verification_state,
              ...(p
                ? { pilot: { deploy_id: d.id, reason: p.reason, pilot_until: p.pilot_until } }
                : {}),
            };
        }
      }
    }
  }

  // Access --------------------------------------------------------------------------------------

  /** The operator principal of `companyId`, or null (404). */
  const operatorIn = (me: MeResponse, companyId: string | undefined) => {
    if (!me.agency || !companyId) return null;
    const p = deps.principalFor(me, companyId.toLowerCase());
    return p?.operator ? p : null;
  };
  const visibleDeploy = (me: MeResponse, id: string | undefined) => {
    const d = deploys.find((x) => x.id === id?.toLowerCase());
    return d && operatorIn(me, d.target_company_id) ? d : null;
  };
  const detailOf = (d: Deploy) => ({
    deploy: d,
    bundle: bundles.find((b) => b.id === d.bundle_id) ?? null,
    verification_override: pilotOf(d),
    awaits_verification_override: d.bundle_id === null && d.state === "staged",
  });
  /** A deploy as its company sees it (template name, version, the pilot's override). */
  const companyDeploy = (d: Deploy): CompanyDeploy => {
    const label = deps.catalog()?.labelOf(d.template_version_id);
    return {
      ...d,
      template_name: label?.template_name ?? "Template",
      version: label?.version ?? 1,
      verification_override: pilotOf(d),
      verification_state: bundles.find((b) => b.id === d.bundle_id)?.verification_state ?? null,
    };
  };
  const sorted = () =>
    [...deploys].sort((a, b) =>
      a.created_at === b.created_at
        ? b.id.localeCompare(a.id)
        : b.created_at.localeCompare(a.created_at),
    );
  const membershipIn = (me: MeResponse, companyId: string) =>
    me.memberships.find((m) => m.company_id === companyId)?.membership_id ?? null;
  const isSupervisor = (p: Principal, de: FakeDe) =>
    membershipIn(p.me, p.company.id) === de.supervisor.membership_id;
  const awaitsApproval = (d: Deploy) =>
    d.state === "staged" && d.path === "done_for_you" && d.kind === "first";

  const once =
    (id: RouteId, handler: Handler, required: boolean): Handler =>
    (ctx: Ctx) => {
      const key = ctx.headers.get("idempotency-key");
      if (required && !key)
        return problem(400, "idempotency_key_required", "Idempotency-Key header required");
      if (key && !UUID.test(key))
        return invalid("Idempotency-Key", "invalid_format", "must be a UUID");
      const k = key ? `${ctx.me.user.id}:${id}:${key}` : null;
      const hit = k ? replays.get(k) : undefined;
      if (hit) return hit.clone();
      const res = handler(ctx);
      if (k && res.status < 500) replays.set(k, res.clone());
      return res;
    };

  function approve(d: Deploy, by: { user?: string; membership?: string }): Response {
    if (!awaitsApproval(d))
      return conflict(
        d.state === "staged"
          ? "only a first deploy of a version into a company waits for approval"
          : `the deploy is ${d.state}`,
      );
    if (d.bundle_id === null)
      return conflict(
        "the version has no verification skill: an operator's verification override builds its bundle first",
      );
    if (pilotOver(d)) return conflict("the pilot's verification override has expired");
    const t = at();
    d.state = "approved";
    d.approved_by_user_id = by.user ?? null;
    d.approved_by_membership_id = by.membership ?? null;
    d.approved_at = t;
    d.updated_at = t;
    return json(d);
  }
  function reject(d: Deploy, body: unknown): Response {
    const reason = (body as { reason?: unknown } | null)?.reason;
    if (typeof reason !== "string" || !reason.trim())
      return invalid("reason", "too_small", "a reason is required");
    if (d.state !== "staged")
      return conflict(`only a staged deploy can be rejected; this one is ${d.state}`);
    d.state = "rejected";
    d.rejected_reason = reason.trim();
    d.updated_at = at();
    return json(d);
  }

  // Routes --------------------------------------------------------------------------------------

  return {
    "operator.deploys.list": ({ me, url }) => {
      if (!me.agency) return notFound();
      tick();
      const company = url.searchParams.get("company_id")?.toLowerCase();
      const state = url.searchParams.get("state");
      const rows = sorted().filter(
        (d) =>
          operatorIn(me, d.target_company_id) &&
          (!company || d.target_company_id === company) &&
          (!state || d.state === state),
      );
      return json(page(rows, url));
    },
    "operator.deploys.get": ({ me, params }) => {
      tick();
      const d = visibleDeploy(me, params.deploy_id);
      return d ? json(detailOf(d)) : notFound();
    },
    "operator.deploys.stage": once(
      "operator.deploys.stage",
      ({ me, body }) => {
        if (!me.agency) return notFound();
        tick();
        const b = (body ?? {}) as Record<string, unknown>;
        for (const f of ["target_company_id", "de_membership_id", "template_version_id"])
          if (typeof b[f] !== "string" || !UUID.test(b[f] as string))
            return invalid(f, "invalid_format", "must be a UUID");
        const extra = Object.keys(b).find(
          (k) =>
            !["target_company_id", "de_membership_id", "template_version_id", "rollback"].includes(
              k,
            ),
        );
        if (extra) return invalid(extra, "unrecognized_keys", `unknown field ${extra}`);
        const companyId = (b.target_company_id as string).toLowerCase();
        if (!operatorIn(me, companyId)) return notFound();
        const de = deOf(companyId, b.de_membership_id as string);
        if (!de || de.status === "retired")
          return problemWith(404, "not_found", "Not found", "no such digital employee");
        const v = deps.catalog()?.versionFor(me, b.template_version_id as string) ?? null;
        if (!v) return problemWith(404, "not_found", "Not found", "no such template version");
        const rollback = b.rollback === true;
        const cls = deps.catalog()?.templateFor(me, v.template_id)?.template_class ?? "managed";
        const pilot = needsPilot(v, cls);
        if (v.state !== "published" && !(rollback && v.state === "deprecated") && !pilot)
          return invalid(
            "template_version_id",
            "version_not_published",
            `a ${v.state} version cannot be deployed; publish it first`,
          );
        const override = overrideFor(companyId, de.membership_id, v.template_id);
        const orders = override?.standing_orders_text ?? v.standing_orders_text;
        const map = override?.role_model_map ?? v.role_model_map;
        const errors: Problemish[] = [];
        if (!orders.trim())
          errors.push({
            path: "standingOrders",
            code: "standing_orders_missing",
            message: "standing orders are required",
          });
        if (Object.keys(map).length === 0)
          errors.push({
            path: "roleModelMap",
            code: "model_map_empty",
            message: "role-to-model map is empty",
          });
        errors.push(...modelMapProblems(map));
        if (errors.length)
          return problemWith(
            422,
            "bundle_refused",
            "The configuration bundle was refused",
            errors.map((e) => e.message).join("; "),
            errors,
          );
        const open = deploys.find(
          (d) =>
            d.target_company_id === companyId &&
            d.de_membership_id === de.membership_id &&
            OPEN.has(d.state),
        );
        if (open)
          return problemWith(
            409,
            "deploy_already_open",
            "This DE already has an open deploy",
            `deploy ${open.id} of this DE is still open`,
          );
        const confirmedHere = deploys.some(
          (d) =>
            d.target_company_id === companyId &&
            d.template_version_id === v.id &&
            d.state === "confirmed",
        );
        if (rollback && !confirmedHere)
          return invalid(
            "rollback",
            "rollback_not_confirmed",
            "a rollback goes to a version already confirmed in this company",
          );
        const previous =
          sorted().find(
            (d) =>
              d.target_company_id === companyId &&
              d.de_membership_id === de.membership_id &&
              d.state === "confirmed",
          ) ?? null;
        const t = at();
        // DC-05: a pilot has no bundle until its verification override builds it.
        const bundle = pilot
          ? null
          : bundleFor(companyId, v.id, override, FILES, operatorUser(me), t);
        const d: Deploy = {
          id: uid(0x7e00, ++seq),
          path: "done_for_you",
          agency_id: me.agency.agency_id,
          target_company_id: companyId,
          de_membership_id: de.membership_id,
          template_id: v.template_id,
          template_version_id: v.id,
          bundle_id: bundle?.id ?? null,
          kind: rollback ? "rollback" : confirmedHere ? "redeploy" : "first",
          state: "staged",
          staged_by: { kind: "user", user_id: operatorUser(me) },
          approved_by_user_id: null,
          approved_by_membership_id: null,
          approved_at: null,
          rejected_reason: null,
          handed_off_at: null,
          handoff_attempts: 0,
          panel_ref: null,
          confirmed_at: null,
          failed_at: null,
          error: null,
          previous_deploy_id: previous?.id ?? null,
          reconcile_state: null,
          reconcile_until: null,
          created_at: t,
          updated_at: t,
        };
        deploys.push(d);
        return json(detailOf(d), 201);
      },
      true,
    ),
    "operator.deploys.approve": once(
      "operator.deploys.approve",
      ({ me, params }) => {
        tick();
        const d = visibleDeploy(me, params.deploy_id);
        return d ? approve(d, { user: me.user.id }) : notFound();
      },
      false,
    ),
    "operator.deploys.reject": once(
      "operator.deploys.reject",
      ({ me, params, body }) => {
        tick();
        const d = visibleDeploy(me, params.deploy_id);
        return d ? reject(d, body) : notFound();
      },
      false,
    ),
    "operator.deploys.overrideVerification": once(
      "operator.deploys.overrideVerification",
      ({ me, params, body }) => {
        tick();
        const d = visibleDeploy(me, params.deploy_id);
        if (!d) return notFound();
        if (!me.agency?.verification_override)
          return forbidden(
            "a verification override needs the agency's verification_override relation",
          );
        const b = (body ?? {}) as Record<string, unknown>;
        const extra = Object.keys(b).find((k) => !["reason", "pilot_until"].includes(k));
        if (extra) return invalid(extra, "unrecognized_keys", `unknown field ${extra}`);
        if (typeof b.reason !== "string" || !b.reason.trim())
          return invalid("reason", "too_small", "a reason is required");
        const until = typeof b.pilot_until === "string" ? Date.parse(b.pilot_until) : Number.NaN;
        if (Number.isNaN(until)) return invalid("pilot_until", "invalid_format", "a timestamp");
        if (pilotOf(d)) return conflict("this deploy already has its verification override");
        const v = deps.catalog()?.versionFor(me, d.template_version_id) ?? null;
        const cls = v ? deps.catalog()?.templateFor(me, v.template_id)?.template_class : undefined;
        if (d.bundle_id !== null || !v || !needsPilot(v, cls ?? ""))
          return problemWith(
            422,
            "verification_override_refused",
            "A verification override is for a pilot of a version without a verification skill",
            "this deploy's version has a verification skill (or is not an managed draft)",
          );
        if (d.state !== "staged")
          return conflict(`only a staged deploy waits for an override; this one is ${d.state}`);
        if (until <= deps.now().getTime())
          return invalid("pilot_until", "pilot_in_past", "the pilot must end in the future");
        const t = at();
        const id = uid(0x7e40, ++seq);
        const override = overrideFor(d.target_company_id, d.de_membership_id, d.template_id);
        const bundle = bundleFor(
          d.target_company_id,
          d.template_version_id,
          override,
          FILES,
          operatorUser(me),
          t,
          id,
        );
        const row: VerificationOverride = {
          id,
          deploy_id: d.id,
          bundle_id: bundle.id,
          target_company_id: d.target_company_id,
          operator_user_id: operatorUser(me),
          reason: b.reason.trim(),
          pilot_until: new Date(until).toISOString(),
          created_at: t,
        };
        pilots.push(row);
        d.bundle_id = bundle.id;
        d.updated_at = t;
        return json(row, 201);
      },
      true,
    ),

    // Company side ------------------------------------------------------------------------------

    "deploys.list": ({ principal, url }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      tick();
      const state = url.searchParams.get("state");
      const rows = sorted()
        .filter(
          (d) => d.target_company_id === principal.company.id && (!state || d.state === state),
        )
        .map(companyDeploy);
      return json(page(rows, url));
    },
    "deploys.get": ({ principal, params }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      tick();
      const d = deploys.find(
        (x) => x.id === params.id?.toLowerCase() && x.target_company_id === principal.company.id,
      );
      return d ? json(companyDeploy(d)) : notFound();
    },
    "deploys.approve": once(
      "deploys.approve",
      ({ principal, params, me }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        tick();
        const d = deploys.find(
          (x) => x.id === params.id?.toLowerCase() && x.target_company_id === principal.company.id,
        );
        if (!d) return notFound();
        const membership = membershipIn(me, principal.company.id);
        if (principal.roles.includes("owner") && membership) return approve(d, { membership });
        if (principal.operator) return approve(d, { user: me.user.id });
        return forbidden("a deploy is approved by the company's owner or its agency operator");
      },
      false,
    ),
    "deploys.reject": once(
      "deploys.reject",
      ({ principal, params, body }) => {
        if (!principal) return notFound();
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        tick();
        const d = deploys.find(
          (x) => x.id === params.id?.toLowerCase() && x.target_company_id === principal.company.id,
        );
        if (!d) return notFound();
        if (!principal.roles.includes("owner") && !principal.operator)
          return forbidden("a deploy is rejected by the company's owner or its agency operator");
        return reject(d, body);
      },
      false,
    ),
    "digitalEmployees.templateOverride": ({ principal, params }) => {
      if (!principal) return notFound();
      const de = deOf(principal.company.id, params.id);
      if (!de) return notFound();
      if (!principal.manager && !isSupervisor(principal, de))
        return problem(403, "forbidden", "Forbidden");
      const mine = overrides
        .filter(
          (o) => o.company_id === principal.company.id && o.de_membership_id === de.membership_id,
        )
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
      const current = de.deploy
        ? deploys.find((d) => d.id === de.deploy?.deploy_ref)?.template_id
        : undefined;
      // As the API: the current template's row, else the template-less one; the newest only
      // while the DE runs no template.
      return json({
        override:
          mine.find((o) => o.template_id === current) ??
          mine.find((o) => o.template_id === null) ??
          (current ? undefined : mine[0]) ??
          null,
        current_template_id: current ?? null,
      });
    },
    // AGENT-MENTION: saved (the override, else the template's own) vs deployed (the version the
    // DE's confirmed deploy runs, with the override it carried then: the bundle's text as built
    // (DE-WIZARD), else — a seeded deploy that predates the twin's list — the version's own).
    "digitalEmployees.standingOrders": ({ principal, params }) => {
      if (!principal) return notFound();
      const de = deOf(principal.company.id, params.id);
      if (!de) return notFound();
      if (!principal.manager && !isSupervisor(principal, de))
        return problem(403, "forbidden", "Forbidden");
      const running = de.deploy ? deploys.find((d) => d.id === de.deploy?.deploy_ref) : undefined;
      const latest =
        running ??
        deploys
          .filter(
            (d) =>
              d.target_company_id === principal.company.id &&
              d.de_membership_id === de.membership_id,
          )
          .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
      const versionId = de.deploy?.template_version_id ?? latest?.template_version_id ?? null;
      const version = versionId ? (deps.catalog()?.versionText(versionId) ?? null) : null;
      const mine = overrides
        .filter(
          (o) => o.company_id === principal.company.id && o.de_membership_id === de.membership_id,
        )
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at));
      // DE-WIZARD: the template's own row, else the DE's own from before any template.
      const saved =
        (version ? mine.find((o) => o.template_id === version.template_id) : undefined) ??
        mine.find((o) => o.template_id === null) ??
        (version ? null : (mine[0] ?? null));
      const templateText = version?.standing_orders_text ?? null;
      const next = saved?.standing_orders_text ?? templateText;
      // The DE's deploy mirror is the truth of what runs (a seeded DE's deploy may predate the
      // twin's deploy list): deployed whenever the mirror is set.
      const live = de.deploy ?? null;
      const builtText =
        running?.bundle_id !== undefined && running?.bundle_id !== null
          ? bundleOrders.get(running.bundle_id)
          : undefined;
      const deployedText = live ? (builtText !== undefined ? builtText : templateText) : null;
      const writer =
        principal.roles.includes("owner") ||
        principal.roles.includes("admin") ||
        isSupervisor(principal, de);
      return json({
        template_id: version?.template_id ?? saved?.template_id ?? null,
        template_name: version?.template_name ?? null,
        template_version_id: versionId,
        template_text: templateText,
        saved: saved
          ? {
              text: saved.standing_orders_text,
              version: saved.version,
              updated_at: saved.updated_at,
            }
          : null,
        next,
        deployed: live
          ? {
              text: deployedText,
              deploy_id: live.deploy_ref,
              bundle_sha: live.bundle_sha,
              deployed_at: running ? (running.confirmed_at ?? running.handed_off_at) : null,
            }
          : null,
        pending: live ? next !== deployedText : next !== null,
        can_edit: writer,
      });
    },
    "digitalEmployees.putTemplateOverride": ({ principal, params, body, me }) => {
      if (!principal) return notFound();
      const de = deOf(principal.company.id, params.id);
      if (!de) return notFound();
      const writer =
        principal.roles.includes("owner") ||
        principal.roles.includes("admin") ||
        isSupervisor(principal, de);
      if (!writer) {
        if (!principal.manager) return problem(403, "forbidden", "Forbidden");
        return forbidden("the override is set by a company admin or owner, or the DE's supervisor");
      }
      const b = (body ?? {}) as Record<string, unknown>;
      // DE-WIZARD: null is the DE's own, before any template is deployed or staged.
      if (
        b.template_id !== null &&
        (typeof b.template_id !== "string" || !UUID.test(b.template_id))
      )
        return invalid("template_id", "invalid_format", "must be a UUID");
      const templateId = typeof b.template_id === "string" ? b.template_id.toLowerCase() : null;
      if (templateId !== null && !deps.catalog()?.templateFor(me, templateId))
        return invalid("template_id", "not_found", "no such agent template");
      const orders = b.standing_orders_text as string | null | undefined;
      const map = b.role_model_map as RoleModelMap | null | undefined;
      if (orders == null && map == null)
        return invalid(
          "body",
          "override_empty",
          "override at least one of standing_orders_text and role_model_map",
        );
      if (map) {
        const problems = modelMapProblems(map);
        if (problems.length)
          return problemWith(
            422,
            "validation_failed",
            "Validation failed",
            problems.map((p) => p.message).join("; "),
            problems,
          );
      }
      const existing = overrides.find(
        (o) =>
          o.company_id === principal.company.id &&
          o.de_membership_id === de.membership_id &&
          o.template_id === templateId,
      );
      const nextOrders = orders === undefined ? (existing?.standing_orders_text ?? null) : orders;
      const nextMap = map === undefined ? (existing?.role_model_map ?? null) : map;
      if (nextOrders === null && nextMap === null)
        return invalid(
          "body",
          "override_empty",
          "override at least one of standing_orders_text and role_model_map",
        );
      const t = at();
      const row: DeTemplateOverride = existing ?? {
        company_id: principal.company.id,
        id: uid(0x7e30, ++seq),
        de_membership_id: de.membership_id,
        template_id: templateId,
        standing_orders_text: null,
        role_model_map: null,
        version: 0,
        actor_kind: "person",
        actor_membership_id: membershipIn(me, principal.company.id),
        created_at: t,
        updated_at: t,
      };
      row.standing_orders_text = nextOrders;
      row.role_model_map = nextMap;
      row.version += 1;
      row.updated_at = t;
      if (!existing) overrides.push(row);
      return json(row);
    },

    // DC-11: self-serve (routes/agents/self-serve.ts) -------------------------------------------

    "agents.selfServeTemplates": ({ principal }) => {
      if (!principal) return notFound();
      if (!principal.manager) return problem(403, "forbidden", "Forbidden");
      return json({ data: deps.catalog()?.selfServe() ?? [] });
    },
    "agents.deploySelfServe": once(
      "agents.deploySelfServe",
      ({ principal, body, me }) => {
        if (!principal) return notFound();
        const membership = membershipIn(me, principal.company.id);
        const stager =
          membership !== null &&
          (principal.roles.includes("owner") || principal.roles.includes("admin"));
        if (!stager)
          return principal.manager
            ? forbidden("a self-serve deploy is staged by the company's owner or admin")
            : problem(403, "forbidden", "Forbidden");
        tick();
        const b = (body ?? {}) as Record<string, unknown>;
        const keys = ["de_membership_id", "template_version_id", "standing_orders_text"];
        const extra = Object.keys(b).find((k) => ![...keys, "role_model_map"].includes(k));
        if (extra) return invalid(extra, "unrecognized_keys", `unknown field ${extra}`);
        for (const f of ["de_membership_id", "template_version_id"])
          if (typeof b[f] !== "string" || !UUID.test(b[f] as string))
            return invalid(f, "invalid_format", "must be a UUID");
        const orders = b.standing_orders_text as string | undefined;
        if (orders !== undefined && (typeof orders !== "string" || !orders.trim()))
          return invalid("standing_orders_text", "too_small", "standing orders must not be empty");
        const map = b.role_model_map as RoleModelMap | undefined;
        if (map !== undefined && (typeof map !== "object" || map === null))
          return invalid("role_model_map", "invalid_type", "a role-to-model map");
        const companyId = principal.company.id;
        const de = deOf(companyId, b.de_membership_id as string);
        if (!de || de.status === "retired")
          return problemWith(404, "not_found", "Not found", "no such digital employee");
        const entry = deps
          .catalog()
          ?.selfServe()
          .find((x) => x.version.id === (b.template_version_id as string).toLowerCase());
        if (!entry)
          return problemWith(404, "not_found", "Not found", "no such self-serve template version");
        const v = entry.version;
        const open = deploys.find(
          (d) =>
            d.target_company_id === companyId &&
            d.de_membership_id === de.membership_id &&
            OPEN.has(d.state),
        );
        if (open)
          return problemWith(
            409,
            "deploy_already_open",
            "This DE already has an open deploy",
            `deploy ${open.id} of this DE is still open`,
          );
        // The body merged into the DE's override (written only when it changes it).
        // DE-WIZARD: over the template's row, else the DE's own from before any template.
        const existing =
          overrides.find(
            (o) =>
              o.company_id === companyId &&
              o.de_membership_id === de.membership_id &&
              o.template_id === v.template_id,
          ) ?? null;
        const base = existing ?? overrideFor(companyId, de.membership_id, v.template_id);
        const changes =
          (orders !== undefined && orders !== base?.standing_orders_text) ||
          (map !== undefined &&
            JSON.stringify(map) !== JSON.stringify(base?.role_model_map ?? null));
        const effOrders = orders ?? base?.standing_orders_text ?? v.standing_orders_text;
        const effMap = map ?? base?.role_model_map ?? v.role_model_map;
        const errors: Problemish[] = [];
        if (!effOrders.trim())
          errors.push({
            path: "standingOrders",
            code: "standing_orders_missing",
            message: "standing orders are required",
          });
        if (Object.keys(effMap).length === 0)
          errors.push({
            path: "roleModelMap",
            code: "model_map_empty",
            message: "role-to-model map is empty",
          });
        errors.push(...modelMapProblems(effMap));
        if (deps.seatHasBudget && !deps.seatHasBudget(companyId, de.seat_id))
          errors.push({
            path: "target.budget",
            code: "budget_missing",
            message: "the seat must have a budget",
          });
        if (errors.length)
          return problemWith(
            422,
            "bundle_refused",
            "The configuration bundle was refused",
            errors.map((e) => e.message).join("; "),
            errors,
          );
        const t = at();
        let override = base;
        if (changes) {
          override = existing ?? {
            company_id: companyId,
            id: uid(0x7e30, ++seq),
            de_membership_id: de.membership_id,
            template_id: v.template_id,
            standing_orders_text: base?.standing_orders_text ?? null,
            role_model_map: base?.role_model_map ?? null,
            version: 0,
            actor_kind: "person",
            actor_membership_id: membership,
            created_at: t,
            updated_at: t,
          };
          if (orders !== undefined) override.standing_orders_text = orders;
          if (map !== undefined) override.role_model_map = map;
          override.version += 1;
          override.updated_at = t;
          if (!existing) overrides.push(override);
        }
        const confirmedHere = deploys.some(
          (d) =>
            d.target_company_id === companyId &&
            d.template_version_id === v.id &&
            d.state === "confirmed",
        );
        const previous =
          sorted().find(
            (d) =>
              d.target_company_id === companyId &&
              d.de_membership_id === de.membership_id &&
              d.state === "confirmed",
          ) ?? null;
        const bundle = bundleFor(
          companyId,
          v.id,
          override,
          FILES,
          me.user.id,
          t,
          undefined,
          entry.template.template_class === "empty",
        );
        const d: Deploy = {
          id: uid(0x7e00, ++seq),
          path: "self_serve",
          agency_id: null,
          target_company_id: companyId,
          de_membership_id: de.membership_id,
          template_id: v.template_id,
          template_version_id: v.id,
          bundle_id: bundle.id,
          kind: confirmedHere ? "redeploy" : "first",
          state: "staged",
          staged_by: { kind: "member", company_id: companyId, membership_id: membership },
          approved_by_user_id: null,
          approved_by_membership_id: null,
          approved_at: null,
          rejected_reason: null,
          handed_off_at: null,
          handoff_attempts: 0,
          panel_ref: null,
          confirmed_at: null,
          failed_at: null,
          error: null,
          previous_deploy_id: previous?.id ?? null,
          reconcile_state: null,
          reconcile_until: null,
          created_at: t,
          updated_at: t,
        };
        deploys.push(d);
        return json(d, 201);
      },
      true,
    ),
  };
}
